import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { RouterModule } from '@angular/router';
import { BreadcrumbService } from '../../../domain/services/breadcrumb/breadcrumb.service';
import { DistributionService } from '../../../domain/services/distribution/distribution.service';
import { DistributionLine, DistributionRun } from '../../../domain/models/distribution.model';

/**
 * Distribution RUNS — the engine output (distribution design section 12 moved this
 * out of the Distributions page, which is now schedule-config only). Admins view the
 * auto-generated (or manually generated) runs here and approve/finalize them, plus
 * download the BOCC summary / collector PDFs. Surfaced under the Reports menu.
 */
@Component({
  selector: 'app-distribution-runs',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, RouterModule, MatCardModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatTableModule, MatProgressBarModule,
    MatDatepickerModule, MatNativeDateModule,
  ],
  templateUrl: './distribution-runs.component.html',
  styleUrl: './distribution-runs.component.css',
})
export class DistributionRunsComponent implements OnInit {
  @ViewChild('detailCard') detailCard?: ElementRef<HTMLElement>;

  runs: DistributionRun[] = [];
  selected: DistributionRun | null = null;
  loading = false;
  busy = false;
  error: string | null = null;
  /** Property ids parsed out of an "unassigned jurisdiction" generation error, for quick edit links. */
  unassignedPropertyIds: number[] = [];

  form: FormGroup;

  readonly lineColumns = [
    'jurisdictionName', 'grossReceipts', 'exemptReceipts', 'taxableReceipts',
    'totalTaxCollected', 'sumAllowance', 'sumPenaltyAmount', 'sumInterestAmount',
    'sumCollectionCommission', 'distributionAmount', 'retained', 'collected',
  ];

  /**
   * Penalty, interest, fees and commission stay with the Tax Collector (PO decision, see
   * TDT-Service/docs/distribution-design.md §2), so the county share alone never matches the
   * Payments report. Same formula as the Collector PDF's TOTAL RETAINED.
   */
  retained(l: DistributionLine): number {
    return (l.sumCollectionCommission ?? 0) + (l.sumPenaltyAmount ?? 0) + (l.sumInterestAmount ?? 0)
      + (l.sumOtherFees ?? 0) + (l.sumProcessingFee ?? 0) + (l.sumOverShort ?? 0);
  }

  /** County share + collector share: what the Payments report shows as collected. */
  collected(l: DistributionLine): number {
    return (l.distributionAmount ?? 0) + this.retained(l);
  }

  /** Footer total for one column; 'retained' / 'collected' are the computed columns above. */
  total(lines: DistributionLine[] | undefined, column: keyof DistributionLine | 'retained' | 'collected'): number {
    return (lines ?? []).reduce((sum, l) => sum + Number(
      column === 'retained' ? this.retained(l) : column === 'collected' ? this.collected(l) : l[column] ?? 0), 0);
  }

  constructor(
    private readonly breadcrumbService: BreadcrumbService,
    private readonly distributionService: DistributionService,
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      startDate: [null as Date | null, Validators.required],
      endDate: [null as Date | null, Validators.required],
      periodLabel: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'pie_chart', url: '/app/distributions', label: 'Distributions' },
      { icon: 'add_chart', url: '/app/distributions/runs', label: 'Run distributions' },
    ]);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.distributionService.list().subscribe({
      next: (runs) => {
        this.runs = runs;
        this.loading = false;
        // Opened from Reports > Distribution (?run=<id>): show that run's detail.
        const wanted = Number(this.route.snapshot.queryParamMap.get('run'));
        const run = wanted && !this.selected ? runs.find(r => r.id === wanted) : undefined;
        if (run) this.select(run);
      },
      error: () => { this.error = 'Failed to load distributions.'; this.loading = false; },
    });
  }

  select(run: DistributionRun): void {
    this.busy = true;
    this.error = null;
    // Optimistically show the row we have so the detail panel appears immediately,
    // then enrich with the full payload (lines) when it arrives.
    this.selected = run;
    this.scrollToDetail();
    this.distributionService.get(run.id).subscribe({
      next: (r) => { this.selected = r; this.busy = false; this.scrollToDetail(); },
      error: () => { this.busy = false; this.error = 'Failed to load distribution detail.'; },
    });
  }

  private scrollToDetail(): void {
    setTimeout(() => this.detailCard?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }

  /** Format a Date as a local YYYY-MM-DD (avoids UTC day-shift from toISOString). */
  private toIso(d: Date | null): string {
    if (!d) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  generate(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    this.busy = true;
    this.error = null;
    this.unassignedPropertyIds = [];
    const v = this.form.value;
    this.distributionService.generate({
      startDate: this.toIso(v.startDate),
      endDate: this.toIso(v.endDate),
      periodLabel: v.periodLabel,
    }).subscribe({
      next: (run) => { this.busy = false; this.form.reset(); this.load(); this.selected = run; },
      error: (e) => {
        this.busy = false;
        this.error = e?.error?.message || 'Generation failed.';
        this.unassignedPropertyIds = this.parseUnassignedPropertyIds(this.error);
      },
    });
  }

  /** Pull distinct property ids out of the backend "unassigned jurisdiction" error string. */
  private parseUnassignedPropertyIds(message: string | null): number[] {
    if (!message) return [];
    const ids = new Set<number>();
    for (const m of message.matchAll(/property=(\d+)/g)) {
      ids.add(Number(m[1]));
    }
    return [...ids];
  }

  approve(): void { this.act(this.distributionService.approve(this.selected!.id)); }
  finalize(): void { this.act(this.distributionService.finalize(this.selected!.id)); }
  rerun(): void { this.act(this.distributionService.rerun(this.selected!.id)); }

  /** Any run that is not FINALIZED can be deleted; a new one can then be generated for the window. */
  canDelete(r: DistributionRun): boolean { return r.status !== 'FINALIZED'; }

  remove(): void {
    const run = this.selected;
    if (!run || !confirm(`Delete "${run.periodLabel}" (${run.startDate} → ${run.endDate})? You can generate the window again afterwards.`)) { return; }
    this.busy = true;
    this.error = null;
    this.distributionService.delete(run.id).subscribe({
      next: () => { this.busy = false; this.selected = null; this.load(); },
      error: (e) => { this.busy = false; this.error = e?.error?.message || 'Delete failed.'; },
    });
  }

  private act(obs: ReturnType<DistributionService['approve']>): void {
    if (!this.selected) { return; }
    this.busy = true;
    this.error = null;
    obs.subscribe({
      next: (run) => { this.busy = false; this.selected = run; this.load(); },
      error: (e) => { this.busy = false; this.error = e?.error?.message || 'Action failed.'; },
    });
  }

  downloadSummary(): void {
    if (this.selected) { this.openBlob(this.distributionService.summaryPdf(this.selected.id)); }
  }
  downloadCollector(): void {
    if (this.selected) { this.openBlob(this.distributionService.collectorPdf(this.selected.id)); }
  }

  private openBlob(obs: ReturnType<DistributionService['summaryPdf']>): void {
    obs.subscribe({
      next: (blob) => window.open(URL.createObjectURL(blob), '_blank'),
      error: () => { this.error = 'Failed to load PDF.'; },
    });
  }

  canApprove(r: DistributionRun): boolean { return r.status === 'READY'; }
  canFinalize(r: DistributionRun): boolean { return r.status === 'APPROVED'; }
  canRerun(r: DistributionRun): boolean { return r.status !== 'FINALIZED'; }
}
